/**
 * ItemRegistry - Centralized item definitions
 * All game items are defined here with their properties and effects
 */

/**
 * Item effect configuration
 */
export interface ItemEffect {
  type: 'revive' | 'heal' | 'buff' | 'damage';
  amount: number; // percentage (0-1) for heal/revive, flat value for damage/buff
  duration?: number; // duration in ms for buffs
  requiresTarget?: boolean;
  targetFilter?: 'fainted' | 'damaged' | 'any';
}

/**
 * Item definition
 */
export interface ItemDefinition {
  id: string;
  name: string;
  description: string;
  type: 'consumable' | 'key' | 'material';
  effect?: ItemEffect;
  stackable: boolean;
  maxStack: number;
  icon?: string; // sprite key for display
}

/**
 * All item definitions
 */
const ITEMS: Record<string, ItemDefinition> = {
  revive_herb: {
    id: 'revive_herb',
    name: 'Revive Herb',
    description: 'A fragrant herb that can revive a fainted beastie to 25% HP.',
    type: 'consumable',
    effect: {
      type: 'revive',
      amount: 0.25,
      requiresTarget: true,
      targetFilter: 'fainted',
    },
    stackable: true,
    maxStack: 10,
  },
  healing_berry: {
    id: 'healing_berry',
    name: 'Healing Berry',
    description: 'A sweet berry that restores 20% HP to a beastie.',
    type: 'consumable',
    effect: {
      type: 'heal',
      amount: 0.20,
      requiresTarget: true,
      targetFilter: 'damaged',
    },
    stackable: true,
    maxStack: 20,
  },
  beastie_treat: {
    id: 'beastie_treat',
    name: 'Beastie Treat',
    description: 'A tasty treat loved by wild beasties. Useful for taming.',
    type: 'consumable',
    stackable: true,
    maxStack: 50,
  },
};

/**
 * ItemRegistry - Static access to item definitions
 */
export class ItemRegistry {
  /**
   * Get an item definition by ID
   */
  public static get(itemId: string): ItemDefinition | undefined {
    return ITEMS[itemId];
  }

  /**
   * Get all item definitions
   */
  public static getAll(): ItemDefinition[] {
    return Object.values(ITEMS);
  }

  /**
   * Check if an item exists
   */
  public static exists(itemId: string): boolean {
    return itemId in ITEMS;
  }

  /**
   * Get all consumable items
   */
  public static getConsumables(): ItemDefinition[] {
    return Object.values(ITEMS).filter((item) => item.type === 'consumable');
  }

  /**
   * Check if an item can be used on a target
   */
  public static canUseOnTarget(
    itemId: string,
    targetState: { isFainted?: boolean; isDamaged?: boolean }
  ): boolean {
    const item = ITEMS[itemId];
    if (!item?.effect) return false;

    const filter = item.effect.targetFilter;
    if (!filter || filter === 'any') return true;

    if (filter === 'fainted' && !targetState.isFainted) return false;
    if (filter === 'damaged' && !targetState.isDamaged) return false;

    return true;
  }
}
